import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import type { SubscriptionTier } from '@/lib/api'
import { BillingLockedTooltip } from '@/components/billing/BillingLockedTooltip'
import { useTranslation } from 'react-i18next'
import { intlLocaleForUi } from '@/i18n'
import {
  CONTACT_SALES_URL,
  type PlanCardConfig,
  type PlanCardId,
} from '@/pages/SettingsPage/components/billing/planConfig'

export type PlanCardCta = {
  kind: 'active' | 'start' | 'upgrade' | 'downgrade' | 'contact-sales'
}

interface PlanCardProps {
  config: PlanCardConfig
  /**
   * Org's real tier. Part of the frozen props contract (both call sites pass
   * it) but no longer read here: every label now comes from `config`, so a
   * card never has to reason about tier strings.
   */
  currentTier: SubscriptionTier
  cta: PlanCardCta
  onCta: (cardId: PlanCardId, action: PlanCardCta['kind']) => void
  /**
   * Locks the CTA: renders it disabled inside `BillingLockedTooltip` with this
   * message. `contact-sales` ignores it (that CTA never hits billing).
   */
  ctaDisabledReason?: string | null
  /** Short headline above the subtitle (e.g. "For solo researchers"). */
  tagline?: string
  /** Marketing line below the credits row (e.g. "Unlimited studies, premium marketplace"). */
  subtitle?: string
  /** Hint shown below the CTA. Disables the button when present. */
  downgradeHint?: string | null
  loading?: boolean
  className?: string
  /** Optional sizing for inner content when the card shell spans a wider column. */
  contentClassName?: string
  /** Optional sizing for the CTA area when only the button should be constrained. */
  ctaClassName?: string
  /**
   * Prefix for `data-testid` attributes: both the Plans page (`pricing-*`)
   * and the Settings billing tab (`billing-*`) call sites depend on their
   * own test-id namespaces. Defaults to `'pricing'`.
   */
  testIdPrefix?: 'pricing' | 'billing'
}

function ctaLabelFor(
  cta: PlanCardCta,
  config: PlanCardConfig,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  switch (cta.kind) {
    case 'active':
      return t('billing.youreOn', { plan: config.displayName })
    case 'start':
      return t('billing.select')
    case 'upgrade':
      return t('billing.upgradeCta')
    case 'downgrade':
      return t('billing.downgrade')
    case 'contact-sales':
      return t('billing.contactSales')
  }
}

/**
 * Shared pricing plan card used by both the Settings billing tab and the
 * dedicated Plans page. Visually mirrors the Plans page (large serif price,
 * tagline + subtitle) and flexes to the width of its grid column.
 *
 * Everything the card renders comes from `config`, so a marketing-only card
 * (Max, which has no subscription tier) renders through exactly the same path
 * as a sellable one.
 */
export function PlanCard(props: PlanCardProps) {
  const {
    config,
    cta,
    onCta,
    ctaDisabledReason,
    tagline,
    subtitle,
    downgradeHint,
    loading,
    className,
    contentClassName,
    ctaClassName,
    testIdPrefix = 'pricing',
  } = props
  const { t, i18n } = useTranslation('app')
  const numberLocale = intlLocaleForUi(i18n.language)

  const isActive = cta.kind === 'active'
  const isContactSales = cta.kind === 'contact-sales'
  // Active cards are already inert; the locked tooltip is only for CTAs a user
  // would otherwise expect to click (start/upgrade/downgrade).
  const isLocked = !!ctaDisabledReason && !isContactSales && !isActive
  const priceUsd = config.monthlyPriceUsd

  function handleCtaClick() {
    if (isContactSales) {
      // The card owns this navigation so every surface sends users to the same
      // place. Call sites therefore only handle their own CTA kinds.
      window.open(config.contactSalesUrl ?? CONTACT_SALES_URL, '_blank', 'noopener,noreferrer')
      return
    }
    onCta(config.id, cta.kind)
  }

  const ctaButton = (
    <Button
      size="sm"
      disabled={!isContactSales && (isActive || loading || !!downgradeHint || isLocked)}
      onClick={handleCtaClick}
      data-testid={`${testIdPrefix}-cta-${cta.kind}-${config.id}`}
      className={cn(
        'w-full rounded-[6px] text-sm font-medium',
        isActive && 'disabled:opacity-100',
        isActive
          ? 'border border-[#0A2120]/40 bg-transparent text-[#0A2120]/60 cursor-default hover:bg-transparent dark:border-[hsl(var(--platform-card))]/40 dark:text-[hsl(var(--platform-card))]/60'
          : cta.kind === 'downgrade'
            ? 'bg-[hsl(var(--platform-card))] text-[hsl(var(--platform-card-foreground))] hover:bg-[hsl(var(--platform-card))]/90'
            : 'bg-[hsl(var(--platform-deep))] text-white hover:bg-[hsl(var(--platform-deep))]/90 dark:bg-[hsl(var(--platform-card))] dark:text-[hsl(var(--platform-card-foreground))] dark:hover:bg-[hsl(var(--platform-card))]/90',
      )}
      variant="default"
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : ctaLabelFor(cta, config, t)}
    </Button>
  )

  return (
    <div
      data-testid={`${testIdPrefix}-plan-card-${config.id}`}
      data-active={isActive ? 'true' : 'false'}
      className={cn(
        'relative flex h-full flex-col gap-5 rounded-xl p-5 transition-colors',
        isActive
          ? 'bg-[linear-gradient(135deg,#D1FFEB_0%,#81E7CC_100%)] dark:bg-[linear-gradient(135deg,#0B2D2E_0%,#00705F_100%)]'
          : 'border border-border bg-background',
        className,
      )}
    >
      <div className={cn('flex w-full flex-col gap-5', contentClassName)}>
        <div className="flex items-center gap-1">
          <img
            src={config.logoSrc}
            alt=""
            aria-hidden
            width={16}
            height={16}
            className="h-4 w-4 shrink-0"
          />
          <span
            className={cn(
              'text-base font-medium leading-tight tracking-tight',
              isActive ? 'text-[#0A2120] dark:text-white' : 'text-foreground',
            )}
          >
            {config.displayName}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-end gap-1">
            <span
              data-testid={`${testIdPrefix}-plan-price-${config.id}`}
              className={cn(
                'font-display text-[38px] leading-[1.2] tabular-nums',
                isActive ? 'text-[#0A2120] dark:text-white' : 'text-foreground',
              )}
            >
              {priceUsd !== null ? `$${priceUsd.toLocaleString(numberLocale)}` : t('billing.custom')}
            </span>
            {priceUsd !== null && (
              <span
                className={cn(
                  'mb-2 text-xs',
                  isActive ? 'text-[#0A2120]/60 dark:text-white/60' : 'text-muted-foreground',
                )}
              >
                {t('billing.perMonth')}
              </span>
            )}
          </div>
          {config.priceCaption ? (
            <span
              data-testid={`${testIdPrefix}-plan-price-caption-${config.id}`}
              className={cn('text-xs', isActive ? 'text-[#0A2120]/60 dark:text-white/60' : 'text-muted-foreground')}
            >
              {t('billing.billedAnnually')}
            </span>
          ) : (
            /* Spacer keeps cards aligned when a card carries no caption
               (e.g. the custom-priced card reading "Custom"). */
            <span aria-hidden className="text-xs">
              &nbsp;
            </span>
          )}
        </div>

        <div className="flex flex-col gap-0.5">
          <span
            className={cn(
              'text-base leading-relaxed',
              isActive ? 'text-[#0A2120] dark:text-white' : 'text-foreground',
            )}
          >
            {config.includedCredits != null
              ? t('billing.creditsPerOrg', {
                  count: config.includedCredits.toLocaleString(numberLocale),
                })
              : t('billing.creditsPerCategory')}
          </span>
        </div>

        <div className="flex flex-col gap-0.5">
          {tagline ? (
            <span
              className={cn(
                'text-sm leading-snug',
                isActive ? 'text-[#0A2120]/60 dark:text-white/60' : 'text-muted-foreground',
              )}
            >
              {tagline}
            </span>
          ) : null}
          {subtitle ? (
            <span
              className={cn(
                'text-base leading-relaxed',
                isActive ? 'text-[#0A2120] dark:text-white' : 'text-foreground',
              )}
            >
              {subtitle}
            </span>
          ) : null}
        </div>
      </div>

      {config.showUnlimitedSeatsPill && (
        <div className={cn('mt-auto w-full', contentClassName)}>
          <span
            className={cn(
              'inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium',
              isActive
                ? 'bg-[#0A2120]/10 text-[#0A2120] dark:bg-white/10 dark:text-white'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {t('billing.unlimitedSeats')}
          </span>
        </div>
      )}

      <div
        className={cn(
          'flex flex-col gap-2',
          // The seats pill already pushes the card's tail to the bottom; only
          // one of the two blocks may claim the free space.
          !config.showUnlimitedSeatsPill && 'mt-auto',
          ctaClassName,
        )}
      >
        {downgradeHint ? (
          <Text
            size="xs"
            color="muted"
            className={cn('text-center', isActive && 'text-[#0A2120]/60 dark:text-white/60')}
            data-testid={`${testIdPrefix}-downgrade-hint-${config.id}`}
          >
            {downgradeHint}
          </Text>
        ) : null}

        {isLocked ? (
          <BillingLockedTooltip message={ctaDisabledReason ?? undefined}>{ctaButton}</BillingLockedTooltip>
        ) : (
          ctaButton
        )}
      </div>
    </div>
  )
}
